import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { ConfirmDialog } from '../../../common/views/modals/confirmDialog/ConfirmDialog';
import { useGetCatalogMergeCandidatesQuery, useGetCatalogQuery } from '../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { mergePair, preselectedMergeUuid } from './mergeRules';
import { mergeMessages } from './mergeMessages';
import { useMergeSongs } from './useMergeSongs';
import { MergePickStep } from './MergePickStep';
import { MergeConfirmStep } from './MergeConfirmStep';

interface MergeSongsModalProps {
  accountId: number;
  /** The row whose menu opened the dialog. Undefined: the dialog is closed. */
  source?: CatalogItem;
  closeModal: () => void;
  /** Gets the row that stays after a merge. */
  onMerged?: (kept: CatalogItem) => void;
}

/**
 * "Merge with another song…" (design catalog-merge a2, a3, a6): pick the row of the same song,
 * then read what stays, what moves and what goes, then merge. The server picks the row that stays
 * (`keepUuid`); the dialog never asks.
 */
const MergeSongsModalComponent: React.FC<MergeSongsModalProps> = ({ accountId, source, closeModal, onMerged }) => {
  const intl = useIntl();
  const [step, setStep] = useState<'pick' | 'confirm'>('pick');
  const [selectedUuid, setSelectedUuid] = useState<string>();
  const candidatesQuery = useGetCatalogMergeCandidatesQuery(
    { accountId, itemUuid: source?.uuid ?? '' },
    { skip: source === undefined, refetchOnMountOrArgChange: true }
  );
  const catalogQuery = useGetCatalogQuery({ accountId }, { skip: source === undefined });
  const { merge, isLoading: isMerging, errorKind, reset } = useMergeSongs({ accountId });
  const candidates = candidatesQuery.data?.candidates;

  // A new source starts the dialog over.
  useEffect(() => {
    setStep('pick');
    setSelectedUuid(undefined);
    reset();
  }, [reset, source?.uuid]);
  useEffect(() => {
    if (selectedUuid === undefined && candidates !== undefined) {
      setSelectedUuid(preselectedMergeUuid(candidates));
    }
  }, [candidates, selectedUuid]);

  const selected = useMemo(() => candidates?.find((row) => row.uuid === selectedUuid), [candidates, selectedUuid]);
  const pair = useMemo(
    () =>
      source === undefined || selected === undefined
        ? undefined
        : mergePair(source, selected, catalogQuery.data?.items ?? []),
    [catalogQuery.data?.items, selected, source]
  );

  const onPickConfirm = useCallback(() => {
    if (candidatesQuery.error !== undefined) {
      candidatesQuery.refetch().catch(console.error);
      return;
    }
    setStep('confirm');
  }, [candidatesQuery]);
  const onBack = useCallback(() => {
    reset();
    setStep('pick');
  }, [reset]);
  const onMerge = useCallback(() => {
    if (pair === undefined) {
      return;
    }
    if (errorKind === 'changed') {
      closeModal();
      return;
    }
    merge(pair.kept.uuid, pair.leaving.title, pair.leaving.uuid)
      .then((kept) => {
        if (kept !== null) {
          closeModal();
          onMerged?.(kept);
        }
      })
      .catch(console.error);
  }, [closeModal, errorKind, merge, onMerged, pair]);

  if (source === undefined) {
    return null;
  }
  if (step === 'confirm' && pair !== undefined) {
    const mergeLabel =
      errorKind === 'changed'
        ? mergeMessages.close
        : errorKind === 'failed'
          ? mergeMessages.tryAgain
          : mergeMessages.merge;
    return (
      <ConfirmDialog
        isOpen
        tone="neutral"
        title={intl.formatMessage(mergeMessages.confirmTitle, { from: pair.leaving.title, title: pair.kept.title })}
        description={intl.formatMessage(mergeMessages.confirmLead, {
          from: pair.leaving.title,
          title: pair.kept.title,
        })}
        confirmLabel={intl.formatMessage(mergeLabel)}
        cancelLabel={intl.formatMessage(mergeMessages.back)}
        footerNote={intl.formatMessage(mergeMessages.noUndo)}
        isBusy={isMerging}
        error={errorKind === undefined ? undefined : intl.formatMessage(mergeMessages[errorKind])}
        onConfirm={onMerge}
        onCancel={onBack}
        testId="catalog-merge-confirm"
      >
        <MergeConfirmStep pair={pair} />
      </ConfirmDialog>
    );
  }
  const loadFailed = candidatesQuery.error !== undefined;
  return (
    <ConfirmDialog
      isOpen
      tone="neutral"
      title={intl.formatMessage(mergeMessages.pickTitle, { title: source.title })}
      description={intl.formatMessage(mergeMessages.pickLead)}
      confirmLabel={intl.formatMessage(loadFailed ? mergeMessages.tryAgain : mergeMessages.pickNext)}
      isConfirmDisabled={!loadFailed && (selected === undefined || catalogQuery.data === undefined)}
      error={loadFailed ? intl.formatMessage(mergeMessages.pickLoadFailed) : undefined}
      onConfirm={onPickConfirm}
      onCancel={closeModal}
      testId="catalog-merge-pick"
    >
      {!loadFailed && (
        <MergePickStep
          candidates={candidates}
          isLoading={candidatesQuery.isLoading}
          selectedUuid={selectedUuid}
          onSelect={setSelectedUuid}
        />
      )}
    </ConfirmDialog>
  );
};

export const MergeSongsModal = TypedMemo(MergeSongsModalComponent);
